/**
 * Floating overlay control panel for pedestrian simulation.
 *
 * Renders above the 3D Canvas (HTML layer) with toggle, spawn, speed, reset
 * controls and a live pedestrian count badge. Also renders a detail popover
 * when a pedestrian is selected.
 */

import { useCallback } from 'react';
import {
  UsersIcon,
  UserPlusIcon,
  Trash2Icon,
  XIcon,
} from 'lucide-react';
import { DesktopTwinPanel } from '@/components/twin/desktop-twin-panel';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import {
  ALL_PEDESTRIAN_ROLES,
  type PedestrianMeta,
  type PedestrianRole,
} from './pedestrian-simulation';

// ── Types ────────────────────────────────────────────────────────────────

export interface PedestrianControlsProps {
  /** Whether the pedestrian simulation layer is visible */
  enabled: boolean;
  onToggle: (value: boolean) => void;
  /** Current number of pedestrians in the simulation */
  count: number;
  /** Spawn a new pedestrian at a random valid position */
  onSpawn: () => void;
  /** Remove all pedestrians */
  onReset: () => void;
  /** Speed multiplier 0.25–3 */
  speed: number;
  onSpeedChange: (value: number) => void;
  /** When supplied, enables conference schedule-driven movement. */
  timelineSync?: boolean;
  onTimelineSyncChange?: (value: boolean) => void;
  /** Shows active navigation routes in the 3D scene. */
  showPaths: boolean;
  onShowPathsChange: (value: boolean) => void;
  /** Roles currently visible in the 3D scene. */
  visibleRoles: readonly PedestrianRole[];
  onVisibleRolesChange: (roles: PedestrianRole[]) => void;
  /** Optional desktop placement override for scenes with another top-left overlay. */
  className?: string;
  /** Optional mobile trigger placement override. */
  mobileClassName?: string;
  /** Optional mobile sheet size override. */
  mobileSheetClassName?: string;
  /** Forces the compact sheet controls for a phone landscape viewport. */
  mobileMode?: boolean;
  /** Currently selected pedestrian (for detail popover) */
  selectedPedestrian: PedestrianMeta | null;
  onDeselectPedestrian: () => void;
}

// ── Role presentation ────────────────────────────────────────────────────

const ROLE_LABELS: Record<PedestrianRole, string> = {
  attendee: 'Attendee',
  exhibitor: 'Exhibitor',
  staff: 'Staff',
  visitor: 'Visitor',
  speaker: 'Speaker',
};

const ROLE_BADGE_VARIANTS: Record<PedestrianRole, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  attendee: 'default',
  exhibitor: 'secondary',
  staff: 'default',
  visitor: 'outline',
  speaker: 'destructive',
};

// ── Component ────────────────────────────────────────────────────────────

export function PedestrianControls({
  enabled,
  onToggle,
  count,
  onSpawn,
  onReset,
  speed,
  onSpeedChange,
  timelineSync,
  onTimelineSyncChange,
  showPaths,
  onShowPathsChange,
  visibleRoles,
  onVisibleRolesChange,
  className,
  mobileClassName,
  mobileSheetClassName,
  mobileMode = false,
  selectedPedestrian,
  onDeselectPedestrian,
}: PedestrianControlsProps) {
  const handleSpeedInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSpeedChange(parseFloat(e.target.value));
    },
    [onSpeedChange],
  );

  const toggleRole = useCallback(
    (role: PedestrianRole, isVisible: boolean) => {
      const nextRoles = isVisible
        ? [...visibleRoles, role]
        : visibleRoles.filter((visibleRole) => visibleRole !== role);
      onVisibleRolesChange(ALL_PEDESTRIAN_ROLES.filter((candidate) => nextRoles.includes(candidate)));
    },
    [onVisibleRolesChange, visibleRoles],
  );

  const controls = (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <UsersIcon className="h-4 w-4 text-indigo-500" />
          <span className="text-xs font-semibold">Pedestrians</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-mono">{count}</Badge>
          <Switch checked={enabled} onCheckedChange={onToggle} aria-label="Toggle pedestrian simulation" />
        </div>
      </div>

      {enabled && (
        <>
          <div className="flex gap-1.5">
            <Button size="sm" className="h-7 flex-1 gap-1 text-xs" onClick={onSpawn}>
              <UserPlusIcon className="h-3.5 w-3.5" />
              Add Person
            </Button>
            <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs" onClick={onReset} disabled={count === 0}>
              <Trash2Icon className="h-3.5 w-3.5" />
              Reset
            </Button>
          </div>

          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground">Walk Speed</span>
              <span className="font-mono text-[10px] text-muted-foreground">{speed.toFixed(1)}×</span>
            </div>
            <input type="range" min={0.25} max={3} step={0.25} value={speed} onChange={handleSpeedInput} className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-muted accent-indigo-500 [&::-moz-range-thumb]:h-3.5 [&::-moz-range-thumb]:w-3.5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-indigo-500 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-indigo-500" />
          </div>

          <div className="flex gap-1">
            {[5, 10, 25].map((n) => (
              <Button key={n} size="sm" variant="ghost" className="h-6 flex-1 px-1 text-[10px]" onClick={() => {
                for (let i = 0; i < n; i += 1) onSpawn();
              }}>
                +{n}
              </Button>
            ))}
          </div>

          <div className="space-y-2 border-t pt-2">
            {onTimelineSyncChange ? (
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] text-muted-foreground">Schedule Timeline Sync</span>
                <Switch checked={timelineSync ?? false} onCheckedChange={onTimelineSyncChange} aria-label="Toggle schedule timeline sync" />
              </div>
            ) : null}
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] text-muted-foreground">Show Path Lines</span>
              <Switch checked={showPaths} onCheckedChange={onShowPathsChange} aria-label="Toggle path line visualization" />
            </div>
          </div>

          <div className="space-y-1.5 border-t pt-2">
            <span className="block text-[10px] text-muted-foreground">Visible roles</span>
            <div className="grid grid-cols-2 gap-x-2 gap-y-1">
              {ALL_PEDESTRIAN_ROLES.map((role) => (
                <label key={role} className="flex cursor-pointer items-center gap-1.5 text-[10px]">
                  <Switch checked={visibleRoles.includes(role)} onCheckedChange={(checked) => toggleRole(role, checked)} aria-label={`Show ${ROLE_LABELS[role]} pedestrians`} className="origin-left scale-75" />
                  <span className="truncate">{ROLE_LABELS[role]}</span>
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      {!mobileMode && (
        <DesktopTwinPanel
          title="Pedestrian controls"
          icon={UsersIcon}
          className={cn('left-4 top-4 z-20', className)}
          panelClassName="w-56 shadow-xl"
          contentClassName="p-1"
        >
          {controls}
        </DesktopTwinPanel>
      )}

      {mobileMode && (
        <Sheet>
          <div className={cn('absolute left-3 top-[4.25rem] z-20', mobileClassName)} onPointerDown={(event) => event.stopPropagation()}>
          <SheetTrigger asChild>
            <Button type="button" size="icon" variant="secondary" className="relative size-11 rounded-xl border bg-card/95 shadow-lg backdrop-blur" aria-label="Open pedestrian controls">
              <UsersIcon className="size-5 text-indigo-500" aria-hidden />
              <Badge className="absolute -right-2 -top-2 min-w-5 justify-center px-1 text-[10px]" variant="secondary">{count}</Badge>
            </Button>
          </SheetTrigger>
          </div>
          <SheetContent side="bottom" className={cn('max-h-[80dvh] overflow-y-auto rounded-t-2xl px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]', mobileSheetClassName)} onPointerDown={(event) => event.stopPropagation()}>
          <SheetHeader>
            <SheetTitle>Pedestrian controls</SheetTitle>
            <SheetDescription>Manage the simulated people and their movement.</SheetDescription>
          </SheetHeader>
          <div className="mt-5">{controls}</div>
          </SheetContent>
        </Sheet>
      )}

      {/* ── Selected pedestrian popover ────────────────────────────── */}
      {selectedPedestrian && !mobileMode && (
        <DesktopTwinPanel
          title={selectedPedestrian.label}
          icon={UsersIcon}
          className="bottom-4 right-4 z-30"
          panelClassName="w-60 shadow-xl"
          contentClassName="space-y-2 p-1"
          headerActions={(
            <Button
              variant="ghost"
              size="icon"
              className="size-8 shrink-0 rounded-lg"
              aria-label="Close pedestrian details"
              onClick={onDeselectPedestrian}
            >
              <XIcon className="h-3.5 w-3.5" />
            </Button>
          )}
        >
          <div className="flex items-center gap-2">
            <span
              className="inline-block h-4 w-4 shrink-0 rounded-full"
              style={{ backgroundColor: selectedPedestrian.color }}
            />
            <span className="text-sm font-semibold leading-tight">{selectedPedestrian.label}</span>
          </div>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Role</span>
              <Badge variant={ROLE_BADGE_VARIANTS[selectedPedestrian.role]}>
                {ROLE_LABELS[selectedPedestrian.role]}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">ID</span>
              <span className="font-mono text-[10px]">{selectedPedestrian.id.slice(0, 16)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Position</span>
              <span className="font-mono text-[10px]">
                ({selectedPedestrian.position[0].toFixed(1)}, {selectedPedestrian.position[2].toFixed(1)})
              </span>
            </div>
          </div>
        </DesktopTwinPanel>
      )}

      {selectedPedestrian && mobileMode && (
        <Sheet open onOpenChange={(open) => { if (!open) onDeselectPedestrian(); }}>
          <SheetContent
            side="bottom"
            className="max-h-[80dvh] overflow-y-auto rounded-t-2xl px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]"
            onPointerDown={(event) => event.stopPropagation()}
          >
            <SheetHeader>
              <SheetTitle>{selectedPedestrian.label}</SheetTitle>
              <SheetDescription>Details for the selected simulated pedestrian.</SheetDescription>
            </SheetHeader>
            <div className="mt-5 space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span
                  className="inline-block h-4 w-4 shrink-0 rounded-full"
                  style={{ backgroundColor: selectedPedestrian.color }}
                />
                <Badge variant={ROLE_BADGE_VARIANTS[selectedPedestrian.role]}>
                  {ROLE_LABELS[selectedPedestrian.role]}
                </Badge>
              </div>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">ID</span>
                  <span className="font-mono text-[10px]">{selectedPedestrian.id.slice(0, 16)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Position</span>
                  <span className="font-mono text-[10px]">
                    ({selectedPedestrian.position[0].toFixed(1)}, {selectedPedestrian.position[2].toFixed(1)})
                  </span>
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      )}
    </>
  );
}
